import classnames from 'classnames';
import capitalize from 'lodash/capitalize';
import ReactTable from 'react-table';
import PropTypes from 'prop-types';
import React from 'react';
import { t } from '../../../../modules/infra/i18n';

import Date from '../../../common/date';
import Money from '../../../common/money';
import Time from '../../../common/Time';

const column = (name, overrides = {}) => ({
  Header: capitalize(name),
  accessor: name,
  id: name,
  ...overrides,
});

const getPaymentLabel = (payment) => {
  const labelMap = {
    pos: 'reports:appointments_report.payment_labels.pos',
    gift_card: 'reports:appointments_report.payment_labels.gift_card',
    cash: 'reports:appointments_report.payment_labels.cash',
    squire: 'reports:appointments_report.payment_labels.squire',
    unpaid: 'reports:appointments_report.payment_labels.unpaid',
  };
  return t(labelMap[payment] || payment);
};

const AppointmentsReport = ({ appointments, filtersDirty, showBarberName, showShopName, showDepositsSecured }) => (
  <div className={classnames({ 'u-dimmed': filtersDirty })}>
    <div display-if={!appointments.length}>{t('reports:appointments_report.no_data')}</div>
    <ReactTable
      display-if={appointments.length}
      data={appointments}
      showPagination
      defaultPageSize={500}
      pageSizeOptions={[5, 10, 25, 50, 100, 500, 1000]}
      minRows={0}
      className="c-table c-table-new c-table-with-sticky-header c-table--small c-no-hover u-mt--"
      previousText={t('common:pagination.previous')}
      nextText={t('common:pagination.next')}
      pageText={t('common:pagination.page')}
      ofText={t('common:pagination.of')}
      rowsText={t('common:pagination.rows')}
      columns={[
        ...(showShopName ? [column('shopName', { Header: t('reports:appointments_report.columns.shop') })] : []),
        {
          Header: t('reports:appointments_report.columns.due_date'),
          accessor: 'dueDateTime',
          id: 'dueDate',
          Cell: props => <Date>{props.value}</Date>,
        },
        {
          Header: t('reports:appointments_report.columns.due_time'),
          accessor: 'dueDateTime',
          id: 'dueTime',
          Cell: props => <Time>{props.value}</Time>,
        },
        {
          Header: t('reports:appointments_report.columns.created_at'),
          accessor: 'createdAt',
          id: 'createdAtDate',
          Cell: props => <Date>{props.value}</Date>,
        },
        {
          Header: t('reports:appointments_report.columns.created_time'),
          accessor: 'createdAt',
          id: 'createdAtTime',
          Cell: props => <Time>{props.value}</Time>,
        },
        {
          Header: t('reports:appointments_report.columns.modified_at'),
          accessor: 'modifiedAt',
          id: 'modifiedAtDate',
          Cell: props => (props.value ? <Date>{props.value}</Date> : <span>{t('reports:appointments_report.values.na')}</span>),
        },
        {
          Header: t('reports:appointments_report.columns.modified_time'),
          accessor: 'modifiedAt',
          id: 'modifiedAtTime',
          Cell: props => (props.value ? <Time>{props.value}</Time> : <span>{t('reports:appointments_report.values.na')}</span>),
        },
        {
          Header: t('reports:appointments_report.columns.modified_by'),
          accessor: 'modifiedBy',
          id: 'modifiedBy',
          Cell: props => <span>{props.value || t('reports:appointments_report.values.na')}</span>,
        },
        {
          Header: t('reports:appointments_report.columns.requested_professional'),
          accessor: 'bookedWithAnyBarber',
          id: 'bookedWithAnyBarber',
          Cell: props => <span>{props.value ? t('reports:appointments_report.values.no') : t('reports:appointments_report.values.yes')}</span>,
        },
        ...(showBarberName
          ? [
              {
                Header: t('reports:appointments_report.columns.barber'),
                accessor: 'barberFirstName',
                id: 'barber',
                Cell: props => (
                  <span>
                    {props.original.barberFirstName} {props.original.barberLastName}
                  </span>
                ),
              },
            ]
          : []),
        {
          Header: t('reports:appointments_report.columns.customer'),
          accessor: 'customerFirstName',
          id: 'customer',
          Cell: props => (
            <span>
              {props.original.customerFirstName} {props.original.customerLastName}
            </span>
          ),
        },
        column('email', { Header: t('reports:appointments_report.columns.email') }),
        column('phone', { Header: t('reports:appointments_report.columns.phone') }),
        column('service', { Header: t('reports:appointments_report.columns.service') }),
        {
          Header: t('reports:appointments_report.columns.service_cost'),
          accessor: 'serviceCost',
          id: 'serviceCost',
          Cell: props => <Money cents={props.value} />,
        },
        ...(showDepositsSecured
          ? [
              {
                Header: t('reports:appointments_report.columns.booking_deposits'),
                accessor: 'depositsSecured',
                id: 'depositsSecured',
                Cell: props => <Money cents={props.value} />,
              },
            ]
          : []),
        {
          Header: t('reports:appointments_report.columns.tip'),
          accessor: 'tip',
          id: 'tipAmount',
          Cell: props => <Money cents={props.value} />,
        },
        {
          Header: t('reports:appointments_report.columns.payment'),
          accessor: 'payment',
          id: 'payment',
          Cell: props => <span>{props.value.map(payment => getPaymentLabel(payment)).join(', ')}</span>,
        },
        column('bookingType', { Header: t('reports:appointments_report.columns.booking_type') }),
        column('status', { Header: t('reports:appointments_report.columns.status') }),
        column('groupId', {
          Header: t('reports:appointments_report.columns.group_id'),
          Cell: props => <span>{props.value ? props.value : t('reports:appointments_report.values.na')}</span>,
        }),
        column('appointmentRating', {
          Header: t('reports:appointments_report.columns.rating'),
          Cell: props => <span>{props.value ? props.value : t('reports:appointments_report.values.na')}</span>,
        }),
      ]}
      getTrProps={(state, rowInfo) => {
        return {
          'data-automation-id': `dataRow:appointment:${rowInfo.index}`,
        };
      }}
      getTdProps={(state, rowInfo, column) => {
        return {
          'data-automation-id': column.id ? `text:${column.id}` : null,
        };
      }}
    />
  </div>
);

AppointmentsReport.propTypes = {
  appointments: PropTypes.array.isRequired,
  filtersDirty: PropTypes.bool.isRequired,
  showBarberName: PropTypes.bool.isRequired,
  showShopName: PropTypes.bool.isRequired,
  showDepositsSecured: PropTypes.bool.isRequired,
};

export default AppointmentsReport;
